import { useEffect, useState, useMemo } from "react";
import api, { eur, eurFull } from "@/lib/api";
import { CheckCircle, Sparkle, Plus, Trash, Handshake, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLOR = { done: "#059669", progress: "#D97706", todo: "#94A3B8" };
const STATUS_LABEL = { done: "Fait", progress: "En cours", todo: "À faire" };
const SYN_TYPES = [
  { v: "commerciale", l: "Commerciale" },
  { v: "operationnelle", l: "Opérationnelle" },
  { v: "financiere", l: "Financière" },
];

export default function IntegrationTab({ cid }) {
  const [integ, setInteg] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [form, setForm] = useState({ type: "commerciale", libelle: "", montant_estime: "" });

  const load = () => api.get(`/integration/${cid}`).then((r) => setInteg(r.data));
  useEffect(() => { load(); }, [cid]);

  const toggle = async (item) => {
    const next = item.status === "done" ? "todo" : item.status === "todo" ? "progress" : "done";
    await api.patch(`/integration/${cid}/checklist`, { item_id: item.id, status: next });
    load();
  };

  const addSyn = async (e) => {
    e.preventDefault();
    if (!form.libelle.trim()) { toast.error("Indiquez un libellé."); return; }
    await api.post(`/integration/${cid}/synergie`, {
      type: form.type, libelle: form.libelle.trim(), montant_estime: Number(form.montant_estime) || 0,
    });
    setForm({ type: "commerciale", libelle: "", montant_estime: "" });
    load();
  };

  const updateSyn = async (sid, patch) => { await api.patch(`/integration/${cid}/synergie/${sid}`, patch); load(); };
  const delSyn = async (sid) => { await api.delete(`/integration/${cid}/synergie/${sid}`); load(); };

  const aiPrefill = async () => {
    setPrefilling(true);
    try {
      const { data } = await api.post(`/integration/${cid}/synergies/ai-prefill`);
      toast.success(`${data.added} synergie(s) estimée(s) ajoutée(s) par l'IA`);
      load();
    } catch { toast.error("Erreur IA synergies"); } finally { setPrefilling(false); }
  };

  const grouped = useMemo(() => {
    if (!integ) return [];
    const m = new Map();
    for (const it of integ.checklist) {
      if (!m.has(it.category)) m.set(it.category, []);
      m.get(it.category).push(it);
    }
    return [...m.entries()];
  }, [integ]);

  if (!integ) return <div className="text-muted-foreground">Chargement…</div>;
  const est = integ.synergies_total_estime || 0;
  const real = integ.synergies_total_realise || 0;
  const taux = est ? Math.round((real / est) * 100) : 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Checklist */}
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-border p-5" data-testid="integration-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="overline flex items-center gap-2"><Handshake size={15} className="text-primary" /> Avancement de l'intégration</span>
            <span className="font-mono text-lg font-bold text-primary">{integ.progress}%</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${integ.progress}%` }} />
          </div>
        </div>
        {grouped.map(([cat, items]) => (
          <div key={cat} className="bg-white border border-border">
            <div className="px-5 py-3 border-b border-border font-head font-bold text-sm">{cat}</div>
            {items.map((item) => (
              <button key={item.id} onClick={() => toggle(item)} data-testid={`integration-item-${item.id}`}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-border last:border-0 hover:bg-secondary text-left">
                <span className="text-sm flex items-center gap-2">
                  <CheckCircle size={18} weight={item.status === "done" ? "fill" : "regular"} style={{ color: STATUS_COLOR[item.status] }} />
                  {item.label}
                </span>
                <span className="overline px-2 py-0.5" style={{ color: STATUS_COLOR[item.status], background: STATUS_COLOR[item.status] + "18" }}>{STATUS_LABEL[item.status]}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Synergies */}
      <div className="space-y-6">
        <div className="bg-white border border-border p-5" data-testid="synergies-summary">
          <div className="flex items-center justify-between mb-3">
            <span className="overline flex items-center gap-2"><TrendUp size={15} className="text-emerald-600" /> Synergies (réalisées vs estimées)</span>
          </div>
          <div className="flex items-end justify-between mb-1">
            <div><div className="text-[11px] text-muted-foreground">Réalisées</div><div className="font-mono text-lg font-bold text-emerald-700">{eur(real)}</div></div>
            <div className="text-right"><div className="text-[11px] text-muted-foreground">Estimées</div><div className="font-mono text-sm">{eur(est)}</div></div>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden mb-1">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, taux)}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground text-right">{taux}% des synergies estimées réalisées</div>
          <button data-testid="ai-prefill-synergies-btn" onClick={aiPrefill} disabled={prefilling}
            className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold bg-[#EEF2FF] text-[#4f46e5] border border-[#c7d2fe] hover:bg-[#e0e7ff] disabled:opacity-50">
            <Sparkle size={15} weight="fill" /> {prefilling ? "Estimation IA…" : "Pré-remplir les synergies (IA)"}
          </button>
        </div>

        <div className="bg-white border border-border p-5">
          <div className="overline text-muted-foreground mb-3">Lignes de synergie</div>
          <div className="space-y-3 mb-4">
            {(integ.synergies || []).length === 0 && <p className="text-xs text-muted-foreground">Aucune synergie suivie. Ajoutez-en ou utilisez l'IA.</p>}
            {(integ.synergies || []).map((s) => (
              <div key={s.id} className="border border-border p-3" data-testid={`synergie-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-[10px] uppercase tracking-wide bg-secondary px-1.5 py-0.5 rounded">{s.type}</span>
                    <div className="text-sm mt-1">{s.libelle}</div>
                  </div>
                  <button data-testid={`synergie-del-${s.id}`} onClick={() => delSyn(s.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash size={14} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <div className="text-[10px] text-muted-foreground">Estimé (€)</div>
                    <div className="font-mono text-sm">{eurFull(s.montant_estime)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground">Réalisé (€)</div>
                    <input type="number" defaultValue={s.montant_realise || 0} data-testid={`synergie-realise-${s.id}`}
                      onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== (s.montant_realise || 0)) updateSyn(s.id, { montant_realise: v }); }}
                      className="w-full px-2 py-1 text-sm border border-border outline-none focus:border-primary font-mono" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={addSyn} className="space-y-2 border-t border-border pt-3">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="synergie-type"
              className="w-full px-2 py-2 text-sm border border-border bg-white outline-none focus:border-primary">
              {SYN_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} placeholder="Libellé de la synergie"
              data-testid="synergie-libelle" className="w-full px-2 py-2 text-sm border border-border outline-none focus:border-primary" />
            <input type="number" value={form.montant_estime} onChange={(e) => setForm({ ...form, montant_estime: e.target.value })} placeholder="Montant estimé (€/an)"
              data-testid="synergie-estime" className="w-full px-2 py-2 text-sm border border-border outline-none focus:border-primary font-mono" />
            <button type="submit" data-testid="add-synergie-btn" className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
              <Plus size={15} /> Ajouter une synergie
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
